import { Link, useLocation } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { cn } from "@/lib/utils";

export interface SettingsSubNavTab {
  label: string;
  href: string;
  icon: LucideIcon;
}

/**
 * Secondary navigation inside a Settings tab that hosts several pages — a pill
 * switcher over its children.
 *
 * Shared by Settings → Config and Settings → Logs. It wraps rather than
 * scrolls, so adding a page can never hide one off the edge.
 */
export function SettingsSubNav({
  tabs,
  section,
  children,
}: {
  tabs: readonly SettingsSubNavTab[];
  /** The Settings tab these pages sit under, for the third breadcrumb. */
  section?: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  // Appends the open page to the trail SettingsLayout already set, so a config
  // page reads Settings › Config › Work Locations rather than stopping at the
  // section.
  const active = tabs.find((tab) => location.pathname === tab.href);
  useSetBreadcrumbs(
    active
      ? [
          { label: "Settings", to: "/dashboard/settings" },
          ...(section ? [{ label: section }] : []),
          { label: active.label },
        ]
      : [{ label: "Settings", to: "/dashboard/settings" }],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-1 rounded-lg bg-muted p-1 w-fit">
        {tabs.map((tab) => {
          const isActive = location.pathname === tab.href;

          return (
            <Link
              key={tab.href}
              to={tab.href}
              role="tab"
              aria-selected={isActive}
              className={cn(
                "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground/80",
              )}
            >
              <tab.icon className={cn("h-4 w-4", isActive ? "text-primary" : "")} />
              <span>{tab.label}</span>
            </Link>
          );
        })}
      </div>

      <div>{children}</div>
    </div>
  );
}
